import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';
import { useIncidents } from 'context/IncidentsContext';
import {
  useUserProfiles,
  shortRoleLabel,
  roleColorClasses,
} from 'context/UserProfilesContext';
import { deriveNameFromEmail, initialsFor } from 'lib/currentUser';

// ---------------------------------------------------------------------------
// TeamActivityFeed
//
// The "who did what" merged timeline that makes collaboration tangible:
//   * students added/updated  →  "Jane Rivera (Teacher) added Marcus B."
//   * IEP goal updates        →  "Aisha Patel (OT) updated a fine-motor goal for Marcus B."
//   * behavior incidents      →  "Kate Owens (Para) logged a high-intensity incident for Marcus B."
//
// Attribution is best-effort — we join on the `created_by` /
// `reported_by` fields the DB already stamps, and fall back to a
// role-neutral phrasing when no user is on file (e.g. a row imported
// before user_profiles existed).
// ---------------------------------------------------------------------------

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < MS_PER_MINUTE) return 'just now';
  if (diff < MS_PER_HOUR) return `${Math.round(diff / MS_PER_MINUTE)} min ago`;
  if (diff < MS_PER_DAY) return `${Math.round(diff / MS_PER_HOUR)} hr ago`;
  const days = Math.round(diff / MS_PER_DAY);
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
}

function iconForType(type) {
  switch (type) {
    case 'student':       return 'UserPlus';
    case 'student-edit':  return 'User';
    case 'goal':          return 'Target';
    case 'goal-mastered': return 'CheckCircle2';
    case 'incident':      return 'AlertTriangle';
    default:              return 'Activity';
  }
}

function borderForPriority(priority) {
  switch (priority) {
    case 'high':   return 'border-l-red-500';
    case 'medium': return 'border-l-yellow-500';
    case 'low':    return 'border-l-emerald-500';
    default:       return 'border-l-slate-300';
  }
}

const TeamActivityFeed = ({ limit = 12 }) => {
  const navigate = useNavigate();
  const { students } = useStudents();
  const { goals } = useIEPGoals();
  const { incidents } = useIncidents();
  const { getUserById } = useUserProfiles();

  const rows = useMemo(() => {
    const studentsById = new Map(
      (Array.isArray(students) ? students : []).map((s) => [s.id, s]),
    );
    const nameFor = (id) => {
      const s = studentsById.get(id);
      if (!s) return 'Unknown student';
      return (
        `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Unnamed student'
      );
    };

    const out = [];

    for (const s of Array.isArray(students) ? students : []) {
      const ts = s.lastUpdated || s.createdAt;
      if (!ts) continue;
      const isNew =
        s.createdAt && Math.abs(new Date(ts) - new Date(s.createdAt)) < 60000;
      out.push({
        id: `student-${s.id}-${ts}`,
        type: isNew ? 'student' : 'student-edit',
        actorId: null, // students table doesn't stamp created_by yet
        verb: isNew ? 'added' : 'updated',
        object: `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'a student',
        subtitle: null,
        timestamp: ts,
        priority: 'low',
        studentId: s.id,
        route: `/student-profile-management/${encodeURIComponent(s.id)}`,
      });
    }

    for (const g of Array.isArray(goals) ? goals : []) {
      const ts = g.lastUpdated || g.createdAt;
      if (!ts) continue;
      const mastered = g.status === 'mastered';
      const label =
        g.goalArea ||
        (g.specific ? String(g.specific).slice(0, 60) : 'an IEP goal');
      out.push({
        id: `goal-${g.id}-${ts}`,
        type: mastered ? 'goal-mastered' : 'goal',
        actorId: g.createdBy || null,
        verb: mastered ? 'marked mastered' : 'updated',
        object: `${label} goal for ${nameFor(g.studentId)}`,
        subtitle:
          g.progressPercent != null
            ? `Progress ${Math.round(Number(g.progressPercent))}%`
            : null,
        timestamp: ts,
        priority: mastered ? 'low' : 'medium',
        studentId: g.studentId,
        goalId: g.id,
        route: '/iep-goals-and-objectives-tracking',
        routeState: { openGoalId: g.id },
      });
    }

    for (const i of Array.isArray(incidents) ? incidents : []) {
      const ts = i.occurredAt || i.createdAt || i.lastUpdated;
      if (!ts) continue;
      const intensity = Number(i.intensity ?? 0);
      const isHigh = intensity >= 4;
      out.push({
        id: `incident-${i.id}-${ts}`,
        type: 'incident',
        actorId: i.reportedBy || null,
        verb: isHigh ? 'logged a high-intensity incident' : 'logged an incident',
        object: `for ${nameFor(i.studentId)}`,
        subtitle: i.behaviorType || null,
        timestamp: ts,
        priority: isHigh ? 'high' : 'medium',
        studentId: i.studentId,
        route: '/behavioral-incident-logging',
      });
    }

    out.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    return out.slice(0, limit);
  }, [students, goals, incidents, limit]);

  const handleClick = (row) => {
    if (!row?.route) return;
    if (row.routeState) navigate(row.route, { state: row.routeState });
    else navigate(row.route);
  };

  return (
    <section className="bg-card rounded-xl border border-border overflow-hidden">
      <header className="p-5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="History" size={18} className="text-primary" />
          <h2 className="text-base font-semibold text-card-foreground">
            Recent team activity
          </h2>
        </div>
        <span className="text-xs text-muted-foreground">
          Every entry your team logs, in one place.
        </span>
      </header>

      <div className="p-5">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-muted/40 py-10 px-4 text-center">
            <Icon name="Sparkles" size={20} className="text-muted-foreground" />
            <p className="text-sm font-medium text-card-foreground">
              Nothing to show yet
            </p>
            <p className="text-xs text-muted-foreground max-w-sm">
              When anyone on the team adds a student, updates an IEP goal, or
              logs a behavior incident, it will show up here for everyone.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {rows.map((row) => {
              const actor = getUserById(row.actorId);
              const actorName =
                actor?.displayName ||
                deriveNameFromEmail(actor?.email) ||
                (row.actorId ? 'A teammate' : 'Someone');
              const actorInitials = initialsFor(actorName);
              const short = shortRoleLabel(actor);
              const actorColor = roleColorClasses(actor);

              return (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => handleClick(row)}
                    className={`w-full text-left border-l-4 ${borderForPriority(
                      row.priority,
                    )} bg-muted/40 hover:bg-muted rounded-r-lg p-3 transition-colors duration-150`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-semibold ${actorColor}`}
                        aria-hidden="true"
                      >
                        {actorInitials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-card-foreground">
                          <span className="font-medium">{actorName}</span>
                          {short && (
                            <span
                              className={`ml-1.5 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${actorColor}`}
                            >
                              {short}
                            </span>
                          )}
                          <span className="text-muted-foreground">
                            {' '}
                            {row.verb}{' '}
                          </span>
                          <span className="font-medium">{row.object}</span>
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                          <Icon
                            name={iconForType(row.type)}
                            size={12}
                            className="text-muted-foreground"
                          />
                          {row.subtitle && (
                            <>
                              <span className="truncate">{row.subtitle}</span>
                              <span>·</span>
                            </>
                          )}
                          <span>{relativeTime(row.timestamp)}</span>
                        </div>
                      </div>
                      <Icon
                        name="ChevronRight"
                        size={16}
                        className="text-muted-foreground/60 mt-1 flex-shrink-0"
                      />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {rows.length >= limit && (
        <footer className="border-t border-border px-5 py-3 text-right">
          <Button
            variant="ghost"
            size="sm"
            iconName="ArrowRight"
            iconPosition="right"
            iconSize={14}
            onClick={() => navigate('/reports-and-analytics')}
          >
            See the full picture in Reports
          </Button>
        </footer>
      )}
    </section>
  );
};

export default TeamActivityFeed;
